import React from 'react';
import { Link } from 'react-router-dom';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';
import { CheckCircle2, Heart, Phone } from 'lucide-react';

const SanitaetsdienstePage = () => {
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <SEO page="sanitaetsdienste" />
      <Navbar />
      <main className="flex-1">
        
        {/* MOBILE: Klassischer Hero wie Dortmund (Bild oben, Titel darunter) */}
        <div className="md:hidden">
          {/* Vollbreites Bild */}
          <div className="w-full">
            <img 
              src="https://customer-assets.emergentagent.com/job_firstaid-bottrop/artifacts/81ivxfzq_472F648F-21ED-4CA5-A98C-0A5E180DA6D9.png"
              alt="Sanitätsdienste"
              className="w-full h-auto object-cover"
              style={{ aspectRatio: '16/9' }}
            />
          </div>
          
          {/* Titel-Bereich unter dem Bild */}
          <div className="bg-white px-4 py-6">
            <h1 className="text-2xl font-bold text-[#1e3a5f] leading-tight mb-3">
              Sanitätsdienste
            </h1>
            <p className="text-slate-600 text-base leading-relaxed">
              Zuverlässige medizinische Betreuung bei Veranstaltungen, Events und betrieblichen Anlässen.
            </p>
          </div>
        </div>

        {/* DESKTOP: Bestehender Hero mit Gradient-Overlay (unverändert) */}
        <div className="hidden md:block">
          <div className="max-w-[1664px] mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <div 
              className="relative overflow-hidden rounded-xl"
              style={{ 
                backgroundImage: `url("https://customer-assets.emergentagent.com/job_firstaid-bottrop/artifacts/81ivxfzq_472F648F-21ED-4CA5-A98C-0A5E180DA6D9.png")`,
                backgroundSize: '100% 100%',
                backgroundPosition: 'center center'
              }}
            >
              {/* Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-r from-[#1e3a5f]/90 via-[#1e3a5f]/70 to-[#1e3a5f]/20" />
              
              {/* Content */}
              <div className="relative px-8 lg:px-10 py-12 lg:py-14">
                <div className="flex flex-row items-center justify-between gap-6 min-h-[240px]">
                  {/* Titel + Zusatzinfo - Links */}
                  <div className="max-w-xl">
                    <div className="flex flex-wrap items-center gap-2 mb-4">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-500 text-white rounded-full text-sm font-semibold shadow-sm">
                        <Heart className="w-3.5 h-3.5" />
                        Medizinische Absicherung
                      </span>
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/95 text-[#1e3a5f] rounded-full text-sm font-semibold shadow-sm">
                        Für Veranstaltungen
                      </span>
                    </div>
                    
                    <h1 className="text-3xl lg:text-4xl font-bold text-white leading-tight drop-shadow-sm">
                      Sanitätsdienste
                    </h1>
                    
                    <p className="text-white/90 text-lg mt-3 drop-shadow-sm">
                      Zuverlässige medizinische Betreuung bei Veranstaltungen, Events und betrieblichen Anlässen.
                    </p>
                    
                    {/* Zusätzlicher Teaser-Text */}
                    <div className="mt-6 p-4 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20">
                      <p className="text-white font-semibold text-lg">
                        Planen Sie eine Veranstaltung?
                      </p>
                      <p className="text-white/80 text-sm mt-1">
                        Wir stellen qualifiziertes Personal für schnelle Hilfe und Sicherheit vor Ort.
                      </p>
                    </div>
                  </div>
                  
                  {/* CTA-Box - Rechts */}
                  <div className="flex-shrink-0 flex items-center">
                    <div className="bg-white/95 backdrop-blur-sm rounded-xl p-5 shadow-xl">
                      <div className="flex items-center gap-3 mb-3">
                        <Phone className="w-6 h-6 text-red-600" />
                        <span className="font-semibold text-[#1e3a5f]">Jetzt anfragen</span>
                      </div>
                      <p className="text-sm text-slate-600 mb-4">Kontaktieren Sie uns für ein individuelles Angebot.</p>
                      <a href="tel:020419878666">
                        <Button className="w-full bg-red-600 hover:bg-red-700 text-white">
                          02041 9878 666
                        </Button>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* INHALT - Same container width as navbar */}
        <section className="py-12 sm:py-16">
          <div className="max-w-[1664px] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="prose prose-lg max-w-none text-slate-600 space-y-6">
              
              <h2 className="text-2xl font-semibold text-[#1e3a5f] mt-8 mb-4">Professionelle Sanitätsdienste für Ihre Veranstaltung</h2>
              
              <p>
                Bei Veranstaltungen, Festen oder betrieblichen Anlässen ist eine medizinische Absicherung 
                oft notwendig oder vorgeschrieben. Unsere Sanitätsdienste sorgen für schnelle Hilfe und 
                Sicherheit vor Ort.
              </p>
              
              <p>
                Wir stellen qualifiziertes Personal, das im Notfall ruhig, professionell und zuverlässig handelt.
              </p>
              
              <h2 className="text-2xl font-semibold text-[#1e3a5f] mt-8 mb-4">Unsere Leistungen</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>Sanitätsdienst bei Veranstaltungen und Events</li>
                <li>Medizinische Erstversorgung vor Ort</li>
                <li>Betreuung von Teilnehmern und Besuchern</li>
                <li>Unterstützung bei Sportveranstaltungen</li>
                <li>Zusammenarbeit mit Rettungsdiensten bei Bedarf</li>
              </ul>
              
              <h2 className="text-2xl font-semibold text-[#1e3a5f] mt-8 mb-4">Wann ist ein Sanitätsdienst sinnvoll?</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>Öffentliche Veranstaltungen</li>
                <li>Firmenveranstaltungen</li>
                <li>Sport- und Vereinsveranstaltungen</li>
                <li>Märkte, Feste und größere Zusammenkünfte</li>
              </ul>
              
              <h2 className="text-2xl font-semibold text-[#1e3a5f] mt-8 mb-4">Warum Erste Hilfe Bottrop?</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>Erfahrenes und geschultes Personal</li>
                <li>Zuverlässige Einsatzplanung</li>
                <li>Freundliches und professionelles Auftreten</li>
                <li>Individuelle Abstimmung auf Ihre Veranstaltung</li>
              </ul>
              
              <p className="mt-8 text-lg font-medium text-[#1e3a5f]">
                Kontaktieren Sie uns für eine individuelle Anfrage und Einsatzplanung.
              </p>
            </div>
            
            <div className="mt-12">
              <Link to="/kontakt">
                <Button className="bg-red-600 hover:bg-red-700 text-white px-8 h-12 text-base font-semibold">
                  Jetzt anfragen
                </Button>
              </Link>
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
};

export default SanitaetsdienstePage;
